import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Pencil, Save, X, Phone, Mail, Building2, Plus, Star, Trash2,
  CheckSquare, Calendar, ExternalLink, Upload, Camera, MoreHorizontal, GitMerge,
} from "lucide-react";
import { CopyButton } from "@/components/CopyButton";
import { ContactLiensSection } from "@/components/ContactLiens";
import { Link } from "wouter";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { Contact, Task } from "@shared/schema";

interface Phone { libelle: string; numero: string; principal: boolean; }

function avatarInitials(prenom: string, nom: string): string {
  return ((prenom?.[0] || "") + (nom?.[0] || "")).toUpperCase() || "?";
}
function avatarColor(name: string): string {
  const colors = ["bg-blue-500","bg-emerald-500","bg-violet-500","bg-orange-500","bg-pink-500","bg-teal-500","bg-indigo-500","bg-amber-500"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}
function orgTypeLabel(type: string | null | undefined): string {
  const map: Record<string, string> = { client: "Client", revendeur: "Revendeur", prospect: "Prospect", fournisseur: "Fournisseur", partenaire: "Partenaire", autre: "Autre" };
  return type ? (map[type] || type) : "";
}
function sourceLabel(source: string | null | undefined): string {
  const map: Record<string, string> = { manuel: "Saisi manuellement", migration_client: "Migré depuis Clients", migration_revendeur: "Migré depuis Revendeurs", migration_prospect: "Migré depuis Prospects" };
  return source ? (map[source] || source) : "—";
}
function taskStatusLabel(status: string): string {
  const map: Record<string, string> = { a_faire: "À faire", en_cours: "En cours", fait: "Fait" };
  return map[status] || status;
}
function taskStatusColor(status: string): string {
  const map: Record<string, string> = { a_faire: "bg-red-100 text-red-800", en_cours: "bg-amber-100 text-amber-800", fait: "bg-emerald-100 text-emerald-800" };
  return map[status] || "";
}

const ORG_TYPE_NONE = "__none__";

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSearch, setMergeSearch] = useState("");
  const [mergeResults, setMergeResults] = useState<Contact[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [editData, setEditData] = useState<Partial<Contact & { telephones: Phone[] }>>({});
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const { data: contact, isLoading } = useQuery<Contact & { organisationNom?: string }>({
    queryKey: ["/api/contacts", id],
    queryFn: async () => {
      const res = await authFetch(`/api/contacts/${id}`);
      if (!res.ok) throw new Error("Contact introuvable");
      return res.json();
    },
  });

  const { data: history = [] } = useQuery<(Task & { role?: string })[]>({
    queryKey: ["/api/contacts", id, "history"],
    queryFn: async () => {
      const res = await authFetch(`/api/contacts/${id}/history`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!contact,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Contact>) => {
      const res = await authFetch(`/api/contacts/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      setEditing(false);
      toast({ title: "Contact mis à jour" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/api/contacts/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      navigate("/contacts");
      toast({ title: "Contact supprimé" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const startEdit = () => {
    if (!contact) return;
    setEditData({
      prenom: contact.prenom,
      nom: contact.nom,
      poste: contact.poste || "",
      email: contact.email || "",
      telephones: (contact.telephones as Phone[]) || [],
      organisationType: contact.organisationType as any,
      organisationLibre: contact.organisationLibre || "",
      notes: contact.notes || "",
      linkedinUrl: (contact as any).linkedinUrl || "",
    });
    setEditing(true);
  };

  const saveEdit = () => updateMutation.mutate(editData as any);

  const searchMergeContacts = async (q: string) => {
    if (!q.trim()) { setMergeResults([]); return; }
    setMergeLoading(true);
    try {
      const res = await authFetch(`/api/contacts?q=${encodeURIComponent(q)}&limit=10`);
      const data: Contact[] = await res.json();
      setMergeResults(data.filter((c: any) => c.id !== id));
    } catch { setMergeResults([]); }
    finally { setMergeLoading(false); }
  };

  const addPhone = () => {
    setEditData(prev => ({
      ...prev,
      telephones: [...(prev.telephones || []), { libelle: "Mobile", numero: "", principal: false }],
    }));
  };

  const updatePhone = (idx: number, field: keyof Phone, value: string | boolean) => {
    setEditData(prev => {
      const tels = [...(prev.telephones || [])];
      tels[idx] = { ...tels[idx], [field]: value };
      if (field === "principal" && value === true) {
        tels.forEach((t, i) => { if (i !== idx) t.principal = false; });
      }
      return { ...prev, telephones: tels };
    });
  };

  const removePhone = (idx: number) => {
    setEditData(prev => ({
      ...prev,
      telephones: (prev.telephones || []).filter((_, i) => i !== idx),
    }));
  };

  const handleAvatarUpload = async (file: File) => {
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authFetch(`/api/contacts/${id}/avatar`, { method: "POST", body: formData });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      await updateMutation.mutateAsync({ avatarUrl: data.url });
    } catch (e: any) {
      toast({ title: "Erreur upload", description: e.message, variant: "destructive" });
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-muted-foreground">Contact introuvable.</p>
        <Link href="/contacts"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Retour</Button></Link>
      </div>
    );
  }

  const telephones = (contact.telephones as Phone[]) || [];
  const editTelephones = (editData.telephones as Phone[]) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link href="/contacts">
          <Button variant="ghost" size="sm" data-testid="btn-back-contacts">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Annuaire
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne gauche sticky */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border bg-card shadow-sm p-6 space-y-4 lg:sticky lg:top-4">
            <div className="flex flex-col items-center gap-3">
              <div className="relative group">
                <Avatar className="h-20 w-20 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                  {contact.avatarUrl && <AvatarImage src={contact.avatarUrl} />}
                  <AvatarFallback className={`text-2xl font-bold text-white ${avatarColor(contact.prenom + contact.nom)}`}>
                    {avatarInitials(contact.prenom, contact.nom)}
                  </AvatarFallback>
                </Avatar>
                <button
                  className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => fileInputRef.current?.click()}
                  title="Changer l'avatar"
                  data-testid="btn-change-avatar"
                >
                  {uploadingAvatar ? (
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="h-5 w-5 text-white" />
                  )}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) handleAvatarUpload(e.target.files[0]); }} />
              </div>

              {editing ? (
                <div className="w-full space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={editData.prenom as string} onChange={e => setEditData(prev => ({ ...prev, prenom: e.target.value }))} placeholder="Prénom" data-testid="input-edit-prenom" />
                    <Input value={editData.nom as string} onChange={e => setEditData(prev => ({ ...prev, nom: e.target.value }))} placeholder="Nom" data-testid="input-edit-nom" />
                  </div>
                  <Input value={editData.poste as string} onChange={e => setEditData(prev => ({ ...prev, poste: e.target.value }))} placeholder="Poste" data-testid="input-edit-poste" />
                </div>
              ) : (
                <div className="text-center">
                  <h2 className="text-xl font-bold" data-testid="text-contact-fullname">{contact.prenom} {contact.nom}</h2>
                  {contact.poste && <p className="text-sm text-muted-foreground">{contact.poste}</p>}
                </div>
              )}
            </div>

            {editing ? (
              <div className="flex gap-2">
                <Button className="flex-1" onClick={saveEdit} disabled={updateMutation.isPending} data-testid="btn-save-contact">
                  <Save className="h-4 w-4 mr-2" />
                  {updateMutation.isPending ? "..." : "Enregistrer"}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={startEdit} data-testid="btn-edit-contact">
                  <Pencil className="h-4 w-4 mr-2" />Modifier
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="btn-more-actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => { setMergeSearch(""); setMergeResults([]); setMergeOpen(true); }} data-testid="btn-merge-contact">
                      <GitMerge className="h-4 w-4 mr-2" />Fusionner avec…
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onClick={() => setDeleteOpen(true)} data-testid="btn-delete-contact">
                      <Trash2 className="h-4 w-4 mr-2" />Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <div className="space-y-2 text-sm">
              {contact.email && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4 shrink-0" />
                  <a href={`mailto:${contact.email}`} className="truncate hover:text-primary transition-colors">{contact.email}</a>
                  <CopyButton value={contact.email} ariaLabel="Copier l'email" size="sm" />
                </div>
              )}
              {telephones.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4 shrink-0" />
                  <a href={`tel:${t.numero}`} className="hover:text-primary transition-colors">{t.numero}</a>
                  <span className="text-xs">({t.libelle})</span>
                  {t.principal && <Star className="h-3 w-3 text-amber-500 fill-amber-500" />}
                  <CopyButton value={t.numero} ariaLabel="Copier le numéro" size="sm" />
                </div>
              ))}
              {(contact.organisationNom || contact.organisationLibre) && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-4 w-4 shrink-0" />
                  <span className="truncate">{contact.organisationNom || contact.organisationLibre}</span>
                  {contact.organisationType && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1 shrink-0">
                      {orgTypeLabel(contact.organisationType)}
                    </Badge>
                  )}
                </div>
              )}
              {(contact as any).linkedinUrl && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <ExternalLink className="h-4 w-4 shrink-0" />
                  <a
                    href={(contact as any).linkedinUrl.startsWith("http") ? (contact as any).linkedinUrl : "https://" + (contact as any).linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate hover:text-primary transition-colors text-sm"
                    data-testid="link-linkedin-sidebar"
                  >
                    LinkedIn
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Colonne droite onglets */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="coordonnees" className="space-y-4">
            <TabsList>
              <TabsTrigger value="coordonnees" data-testid="tab-coordonnees">Coordonnées</TabsTrigger>
              <TabsTrigger value="organisation" data-testid="tab-organisation">Organisation</TabsTrigger>
              <TabsTrigger value="historique" data-testid="tab-historique">
                Historique
                {history.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{history.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="meta" data-testid="tab-meta">Métadonnées</TabsTrigger>
            </TabsList>

            {/* ─── Coordonnées ─── */}
            <TabsContent value="coordonnees" className="space-y-4">
              <div className="rounded-xl border bg-card p-5 space-y-5">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  {editing ? (
                    <Input type="email" value={editData.email as string} onChange={e => setEditData(prev => ({ ...prev, email: e.target.value }))} placeholder="jean.dupont@exemple.fr" data-testid="input-edit-email" />
                  ) : (
                    <div className="flex items-center gap-2 text-sm" data-testid="detail-email">
                      {contact.email ? <a href={`mailto:${contact.email}`} className="hover:underline text-primary">{contact.email}</a> : <span>—</span>}
                      {contact.email && <CopyButton value={contact.email} ariaLabel="Copier l'email" size="sm" />}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">Téléphones</Label>
                    {editing && (
                      <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={addPhone}>
                        <Plus className="h-3 w-3 mr-1" />Ajouter
                      </Button>
                    )}
                  </div>
                  {editing ? (
                    <div className="space-y-2">
                      {editTelephones.map((t, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input value={t.libelle} onChange={e => updatePhone(i, "libelle", e.target.value)} placeholder="Mobile" className="w-[110px]" />
                          <Input value={t.numero} onChange={e => updatePhone(i, "numero", e.target.value)} placeholder="+33 6 12 34 56" className="flex-1" />
                          <button type="button" title="Principal" onClick={() => updatePhone(i, "principal", true)} className={t.principal ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}>
                            <Star className={`h-4 w-4 ${t.principal ? "fill-amber-500" : ""}`} />
                          </button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removePhone(i)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                      {editTelephones.length === 0 && (
                        <p className="text-xs text-muted-foreground">Aucun téléphone. Cliquez sur Ajouter.</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {telephones.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : telephones.map((t, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <a href={`tel:${t.numero}`} className="hover:underline text-primary">{t.numero}</a>
                          <span className="text-xs text-muted-foreground">({t.libelle})</span>
                          {t.principal && <Star className="h-3 w-3 text-amber-500 fill-amber-500" />}
                          <CopyButton value={t.numero} ariaLabel="Copier le numéro" size="sm" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Profil LinkedIn</Label>
                  {editing ? (
                    <Input
                      value={(editData as any).linkedinUrl as string}
                      onChange={e => setEditData(prev => ({ ...prev, linkedinUrl: e.target.value }))}
                      placeholder="linkedin.com/in/jean-dupont"
                      data-testid="input-edit-linkedin"
                    />
                  ) : (
                    <div className="text-sm" data-testid="detail-linkedin">
                      {(contact as any).linkedinUrl ? (
                        <a
                          href={(contact as any).linkedinUrl.startsWith("http") ? (contact as any).linkedinUrl : "https://" + (contact as any).linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline text-primary flex items-center gap-1"
                        >
                          {(contact as any).linkedinUrl}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : <span className="text-muted-foreground">—</span>}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  {editing ? (
                    <Textarea value={editData.notes as string} onChange={e => setEditData(prev => ({ ...prev, notes: e.target.value }))} rows={4} placeholder="Informations complémentaires..." data-testid="input-edit-notes" />
                  ) : (
                    <p className="text-sm whitespace-pre-wrap text-muted-foreground" data-testid="detail-notes">
                      {contact.notes || "—"}
                    </p>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ─── Organisation ─── */}
            <TabsContent value="organisation">
              <div className="rounded-xl border bg-card p-5 space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  {editing ? (
                    <Select value={(editData.organisationType as string | null) || ORG_TYPE_NONE} onValueChange={v => setEditData(prev => ({ ...prev, organisationType: v === ORG_TYPE_NONE ? null : v as any }))}>
                      <SelectTrigger data-testid="select-edit-org-type"><SelectValue placeholder="Type d'organisation" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ORG_TYPE_NONE}>Non défini</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="revendeur">Revendeur</SelectItem>
                        <SelectItem value="prospect">Prospect</SelectItem>
                        <SelectItem value="fournisseur">Fournisseur</SelectItem>
                        <SelectItem value="partenaire">Partenaire</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm">{orgTypeLabel(contact.organisationType) || "—"}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Organisation liée</Label>
                  {contact.organisationNom && contact.organisationId && contact.organisationType ? (
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{contact.organisationNom}</p>
                      <Link href={`/${contact.organisationType === "client" ? "clients" : contact.organisationType === "revendeur" ? "resellers" : "prospects"}/${contact.organisationId}`}>
                        <Button variant="ghost" size="icon" className="h-6 w-6" title="Ouvrir la fiche">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </Link>
                    </div>
                  ) : editing ? (
                    <Input value={editData.organisationLibre as string || ""} onChange={e => setEditData(prev => ({ ...prev, organisationLibre: e.target.value }))} placeholder="Nom libre de l'organisation" data-testid="input-edit-org-libre" />
                  ) : (
                    <p className="text-sm">{contact.organisationLibre || "—"}</p>
                  )}
                </div>

                <div className="pt-1 border-t">
                  <ContactLiensSection contactId={contact.id} />
                </div>
              </div>
            </TabsContent>

            {/* ─── Historique ─── */}
            <TabsContent value="historique">
              <div className="rounded-xl border bg-card p-5">
                {history.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                    <CheckSquare className="h-8 w-8" />
                    <p className="text-sm">Ce contact n'est lié à aucune action pour l'instant.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((task: any) => (
                      <div key={task.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/30 transition-colors" data-testid={`history-task-${task.id}`}>
                        <div className="mt-0.5">
                          <CheckSquare className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium truncate">{task.titre}</p>
                            <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${taskStatusColor(task.status)}`}>
                              {taskStatusLabel(task.status)}
                            </span>
                            {task.role && (
                              <Badge variant="outline" className="text-[10px] h-4 px-1 shrink-0">{task.role}</Badge>
                            )}
                          </div>
                          {task.dateEcheance && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(task.dateEcheance + "T00:00:00"), "dd MMMM yyyy", { locale: fr })}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ─── Métadonnées ─── */}
            <TabsContent value="meta">
              <div className="rounded-xl border bg-card p-5 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Source</Label>
                    <p className="mt-1">{sourceLabel(contact.source)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Créé le</Label>
                    <p className="mt-1">
                      {contact.createdAt ? format(new Date(contact.createdAt), "dd MMM yyyy", { locale: fr }) : "—"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Dernière modification</Label>
                    <p className="mt-1">
                      {contact.updatedAt ? format(new Date(contact.updatedAt), "dd MMM yyyy HH:mm", { locale: fr }) : "—"}
                    </p>
                  </div>
                  {contact.lastSyncedAt && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Dernière synchro</Label>
                      <p className="mt-1">{format(new Date(contact.lastSyncedAt), "dd MMM yyyy HH:mm", { locale: fr })}</p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <Label className="text-xs text-muted-foreground">ID technique</Label>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">{contact.id}</p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent onCloseAutoFocus={e => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {contact.prenom} {contact.nom} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le contact sera retiré de toutes les tâches associées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate()}
              data-testid="btn-confirm-delete"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={mergeOpen} onOpenChange={v => { setMergeOpen(v); if (!v) setMergeResults([]); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-primary" />
              Fusionner avec un autre contact
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Recherchez le contact à fusionner avec <strong>{contact.prenom} {contact.nom}</strong>.
            </p>
            <div className="relative">
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Nom, email, société…"
                value={mergeSearch}
                onChange={e => { setMergeSearch(e.target.value); searchMergeContacts(e.target.value); }}
                data-testid="input-merge-search"
                autoFocus
              />
              {mergeLoading && <div className="absolute right-3 top-2.5 h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
            </div>
            {mergeResults.length > 0 && (
              <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                {mergeResults.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-muted/50 text-left transition-colors"
                    onClick={() => { setMergeOpen(false); navigate(`/contacts/fusion?a=${id}&b=${c.id}`); }}
                    data-testid={`btn-select-merge-${c.id}`}
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${["bg-blue-500","bg-emerald-500","bg-violet-500","bg-orange-500"][Math.abs((c.prenom+c.nom).split("").reduce((h,ch)=>ch.charCodeAt(0)+((h<<5)-h),0))%4]}`}>
                      {((c.prenom?.[0]||"")+(c.nom?.[0]||"")).toUpperCase()||"?"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.prenom} {c.nom}</p>
                      {c.email && <p className="text-xs text-muted-foreground truncate">{c.email}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {mergeSearch.trim() && !mergeLoading && mergeResults.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-3">Aucun contact trouvé</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
